import { Component, forceUpdate, h, Host, Prop, State } from "@stencil/core";
import { getCountryName, i18nProvider } from "@trumpf/ux.i18n";
import { fetchData } from "./api";

@Component({
    tag: "ux-iws-sf-page-header",
    styleUrl: "sf-page-header.scss",
    shadow: false,
})
export class SfPageHeader {
    private static cachedNavigationHtml: string = "";
    private i18nSubscriberId: string = "";
    private navigationDataIncrement = 0;

    /** API endpoint used to fetch the navigation */
    @Prop() endpoint: string = "";

    /** Locale used to fetch data and translate texts */
    @Prop() locale: string = "de_DE";

    /** Link url for the TRUMPF logo */
    @Prop() logoLinkUrl: string = "/";

    /** Link text for the TRUMPF logo */
    @Prop() logoLinkText: string = "Trumpf";

    /** Link url for the site menu item */
    @Prop() homeLinkUrl: string = "";

    /** Link text for the site menu item */
    @Prop() homeLinkText: string = "";

    /** Text for the profile menu item */
    @Prop() profileLinkText: string = "";

    /** Storage for the fetched navigation HTML */
    @State() navigationHtml: string = "";

    componentWillLoad() {
        if (this.locale) {
            i18nProvider.changeLanguage(this.locale.replace("_", "-"));
        }
        if (SfPageHeader.cachedNavigationHtml) {
            this.navigationDataIncrement++;
            this.navigationHtml = SfPageHeader.cachedNavigationHtml;
        }
        if (this.endpoint) {
            fetchData(this.endpoint, this.locale).then((html) => {
                this.navigationDataIncrement++;
                this.navigationHtml = html;
                SfPageHeader.cachedNavigationHtml = html;
            });
        }
    }

    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
    }

    disconnectedCallback() {
        i18nProvider.unsubscribe(this.i18nSubscriberId);
    }

    private getCountryLabel = (): string => {
        const countryCode = this.locale
            .replace("-", "_")
            .split("_")[1]
            .toUpperCase();
        const countryName = getCountryName(countryCode);
        return countryName ? `${countryName} | ${countryCode}` : countryCode;
    };

    private handleClickMetaNavLink = (event: Event) => {
        event.preventDefault();
        // TODO: emit custom event
    };

    private renderCountryLink = () => {
        const countryLabel = this.getCountryLabel();
        return (
            <div class="ux-iws-navigation-meta__item ux-iws-navigation-meta__item">
                <a
                    href="#"
                    onClick={this.handleClickMetaNavLink}
                    data-meta-navigation="locale-selection"
                >
                    <span class="ux-iws-navigation-meta__icon-box">
                        <ux-icon name="nav-globe"></ux-icon>
                    </span>
                    <span class="ux-iws-navigation-meta__link-text">
                        {countryLabel}
                    </span>
                </a>
            </div>
        );
    };

    private renderProfileLink = () => {
        if (!this.profileLinkText) {
            return null;
        }
        return (
            <div class="ux-iws-navigation-meta__item ux-iws-navigation-meta__item">
                <a
                    href="#"
                    onClick={this.handleClickMetaNavLink}
                    data-meta-navigation="profile"
                >
                    <span class="ux-iws-navigation-meta__icon-box">
                        <ux-icon name="avatar-circle"></ux-icon>
                    </span>
                    <span class="ux-iws-navigation-meta__link-text">
                        {this.profileLinkText}
                    </span>
                </a>
            </div>
        );
    };

    private renderHomeLink = (dropdown: boolean = false) => {
        if (!(this.homeLinkUrl && this.homeLinkText)) {
            return null;
        }
        return dropdown ? (
            <div class="ux-iws-navigation-meta__item">
                <a class="ux-button ux-button__trumpf" href={this.homeLinkUrl}>
                    {this.homeLinkText}
                    <ux-icon name="chevron-right"></ux-icon>
                </a>
            </div>
        ) : (
            <div class="ux-iws-navigation-meta__item ux-iws-navigation-meta__item--top-border">
                <a href={this.homeLinkUrl}>
                    <span class="ux-iws-navigation-meta__icon-box">
                        <ux-icon name="nav-home"></ux-icon>
                    </span>
                    <span class="ux-iws-navigation-meta__link-text">
                        {this.homeLinkText}
                    </span>
                </a>
            </div>
        );
    };

    render() {
        const { t } = i18nProvider;
        const menuLabel = t("app.header.menuLabel", "Menu");
        const closeLabel = t("app.header.closeButton", "Close");
        const backLabel = t("general.prevButton", "Back");
        return (
            <Host>
                <ux-iws-header type="mytrumpf">
                    <div slot="header" class="ux-iws-header__main-wrapper">
                        <a
                            class="ux-iws-header__logo"
                            href={this.logoLinkUrl}
                            aria-label={this.logoLinkText}
                        >
                            <img
                                src="https://www.trumpf.com/typo3conf/ext/trumpf_package/Resources/Public/Images/nav/logo.png"
                                alt="Trumpf logo"
                            />
                        </a>
                        <div class="ux-iws-header__navigation-wrapper">
                            <ux-iws-nav
                                back-button-text={backLabel}
                                key={
                                    this.navigationHtml
                                        ? `nav-${this.navigationDataIncrement}`
                                        : "loading"
                                }
                            >
                                <nav
                                    slot="navigation"
                                    innerHTML={this.navigationHtml}
                                />
                                <div
                                    slot="navigation-meta"
                                    class="ux-iws-nav__meta"
                                >
                                    <div class="ux-iws-nav__meta-container-header">
                                        <ux-iws-navigation-meta>
                                            {this.renderProfileLink()}
                                            {this.renderCountryLink()}
                                            {this.renderHomeLink()}
                                        </ux-iws-navigation-meta>
                                    </div>
                                    <div class="ux-iws-nav__meta-container-burger">
                                        <ux-iws-nav-burger
                                            menu-text-open={menuLabel}
                                            menu-text-close={closeLabel}
                                        ></ux-iws-nav-burger>
                                    </div>
                                </div>
                            </ux-iws-nav>
                        </div>
                    </div>
                    <div
                        slot="header-dropdown"
                        id="ux-iws-header__dropdown-container"
                        class="ux-iws-header__dropdown-container"
                    >
                        <div class="ux-iws-header__meta-container-dropdown">
                            <ux-iws-navigation-meta>
                                {this.renderProfileLink()}
                                {this.renderCountryLink()}
                                <span class="ux-iws-navigation-meta__item__flex-break"></span>
                                {this.renderHomeLink(true)}
                            </ux-iws-navigation-meta>
                        </div>
                    </div>
                </ux-iws-header>
            </Host>
        );
    }
}
